/**
 * 설정 > 감사 로그.
 * Figma「07 감사 로그」(12679:4579) — auditLogService 서버 페이지네이션 연동.
 * 행 클릭 시 하단 상세 카드에 changes 기반 변경 항목을 표시한다.
 */
import { useAuditLogList } from '@/entities/admin-user/hooks/useAdminUsers';
import type {
  AdminAuditLogEventType,
  AdminAuditLogListParams,
} from '@/entities/admin-user/types';
import { DataCard } from '@/shared/components/DataCard';
import { EmptyState } from '@/shared/components/EmptyState';
import { FilterCard } from '@/shared/components/FilterCard';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { PageHeader } from '@/shared/components/PageHeader';
import { SimplePagination } from '@/shared/components/SimplePagination';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Field } from '@/shared/components/ui/form-field';
import { Input } from '@/shared/components/ui/input';
import { FilterRow } from '@/shared/components/ui/row';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { cn } from '@/shared/lib/utils';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { ChevronRight, ScrollText, Search } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import {
  AUDIT_EVENT_FILTER_OPTIONS,
  AUDIT_PERIOD_OPTIONS,
  getAuditEventBadgeProps,
  getAuditEventLabel,
  mapAdminAuditLogToRow,
  toAuditLogDateRange,
  type AuditLogRow,
  type AuditPeriodFilter,
} from './settingsAuditLogData';
import { AuditChangesDetail } from './AuditChangesDetail';

const PAGE_SIZE = 100;

export const SettingsAuditLogPage: React.FC = () => {
  const toast = useToast();
  const [period, setPeriod] = useState<AuditPeriodFilter>('7d');
  const [eventFilter, setEventFilter] = useState('all');
  const [actorInput, setActorInput] = useState('');
  const [appliedActor, setAppliedActor] = useState('');
  const [queryInput, setQueryInput] = useState('');
  const [appliedQuery, setAppliedQuery] = useState('');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const listParams = useMemo<AdminAuditLogListParams>(() => {
    const range = toAuditLogDateRange(period);
    return {
      ...range,
      eventType:
        eventFilter === 'all'
          ? undefined
          : (eventFilter as AdminAuditLogEventType),
      actorUsername: appliedActor.trim() || undefined,
      targetQuery: appliedQuery.trim() || undefined,
      // Spring Page 0-based ← SimplePagination 1-based
      page: Math.max(0, page - 1),
      size: PAGE_SIZE,
    };
  }, [period, eventFilter, appliedActor, appliedQuery, page]);

  const { data, isLoading, isError, error } = useAuditLogList(listParams);

  useEffect(() => {
    if (isError && error) {
      handleApiError(error, toast);
    }
  }, [isError, error, toast]);

  // 필터·페이지 변경 시 상세 선택 초기화
  useEffect(() => {
    setSelectedId(null);
  }, [period, eventFilter, appliedActor, appliedQuery, page]);

  const pageItems = useMemo(
    () => (data?.content ?? []).map(mapAdminAuditLogToRow),
    [data?.content],
  );
  const total = data?.totalCount ?? 0;
  const selectedRow = useMemo(
    () => pageItems.find((row) => row.id === selectedId) ?? null,
    [pageItems, selectedId],
  );

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setAppliedQuery(queryInput);
    setAppliedActor(actorInput);
    setPage(1);
  };

  const handleSelectRow = (row: AuditLogRow) => {
    setSelectedId(row.id);
  };

  return (
    <div className="space-y-5">
      <PageHeader
        icon={ScrollText}
        title="감사 로그"
        description="계정·권한 변경 이력을 조회합니다"
      />

      <FilterCard>
        <FilterRow gap={12} onSubmit={handleSearch}>
          <div className="flex flex-wrap items-end gap-3">
            <Field label="기간" htmlFor="audit-period">
              <Select
                value={period}
                onValueChange={(value) => {
                  setPeriod(value as AuditPeriodFilter);
                  setPage(1);
                }}
              >
                <SelectTrigger id="audit-period" className="w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AUDIT_PERIOD_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="이벤트" htmlFor="audit-event">
              <Select
                value={eventFilter}
                onValueChange={(value) => {
                  setEventFilter(value);
                  setPage(1);
                }}
              >
                <SelectTrigger id="audit-event" className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AUDIT_EVENT_FILTER_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field
              label="수행 계정"
              htmlFor="audit-actor"
              className="w-full min-w-[160px] sm:w-[200px]"
            >
              <Input
                id="audit-actor"
                value={actorInput}
                onChange={(e) => setActorInput(e.target.value)}
                placeholder="수행자 유저명 검색"
              />
            </Field>

            <Field
              label="대상 계정"
              htmlFor="audit-target-search"
              className="w-full min-w-[200px] sm:w-[240px]"
            >
              <Input
                id="audit-target-search"
                value={queryInput}
                onChange={(e) => setQueryInput(e.target.value)}
                placeholder="대상 유저명 검색"
                startIcon={<Search />}
              />
            </Field>

            <Button type="submit" className="ruda-button shrink-0 self-end">
              검색
            </Button>
          </div>
        </FilterRow>
      </FilterCard>

      <DataCard>
        {isLoading ? (
          <LoadingSpinner tip="감사 로그를 불러오는 중..." />
        ) : pageItems.length === 0 ? (
          <EmptyState description="조건에 맞는 감사 로그가 없습니다." />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[160px]">일시</TableHead>
                  <TableHead className="w-[140px]">이벤트</TableHead>
                  <TableHead>수행 유저</TableHead>
                  <TableHead>대상 유저</TableHead>
                  <TableHead className="w-[140px]">대상 메뉴</TableHead>
                  <TableHead>변경 내용</TableHead>
                  <TableHead className="w-[110px]">IP</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageItems.map((row) => {
                  const badge = getAuditEventBadgeProps(row.event);
                  const selected = selectedId === row.id;
                  return (
                    <TableRow
                      key={row.id}
                      className={cn(
                        'cursor-pointer transition-colors hover:bg-muted/40',
                        selected && 'bg-muted/60',
                      )}
                      onClick={() => handleSelectRow(row)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleSelectRow(row);
                        }
                      }}
                      aria-selected={selected}
                      tabIndex={0}
                    >
                      <TableCell className="text-muted-foreground">
                        {row.at}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={badge.variant}
                          className={badge.className}
                        >
                          {getAuditEventLabel(row.event)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {row.actorDisplay}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {row.targetDisplay}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {row.targetMenuName}
                      </TableCell>
                      <TableCell className="max-w-[320px] text-foreground">
                        <div className="flex items-center justify-between gap-3">
                          <span className="truncate">{row.summary}</span>
                          <span className="shrink-0 text-xs text-ruda-primary">
                            상세보기
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {row.ip}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            <div className="px-4 pt-3">
              <p className="text-xs text-ruda-text-tertiary">
                행을 클릭하면 우측 패널에서 변경 이력을 확인할 수 있습니다 · 감사 로그는
                수정·삭제할 수 없습니다
              </p>
            </div>
            <SimplePagination
              page={page}
              pageSize={PAGE_SIZE}
              total={total}
              onPageChange={setPage}
            />
          </>
        )}
      </DataCard>

      <Dialog
        open={selectedRow != null}
        onOpenChange={(open) => {
          if (!open) setSelectedId(null);
        }}
      >
        {selectedRow ? (
          <DialogContent className="fixed inset-y-0 right-0 left-auto top-0 flex h-dvh max-h-dvh w-full max-w-[960px] translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-none border-0 p-0 sm:max-w-[880px]">
            <DialogHeader className="items-start border-b border-border px-6 py-5 text-left">
              <div className="flex flex-wrap items-center gap-2 pr-10">
                <DialogTitle>감사 로그 상세</DialogTitle>
                <Badge
                  variant={getAuditEventBadgeProps(selectedRow.event).variant}
                  className={getAuditEventBadgeProps(selectedRow.event).className}
                >
                  {getAuditEventLabel(selectedRow.event)}
                </Badge>
              </div>
              <DialogDescription className="text-left">
                {selectedRow.at} 발생 이력입니다. 목록을 벗어나지 않고 변경 전후를
                비교할 수 있도록 우측 패널에서 상세 내용을 제공합니다.
              </DialogDescription>
            </DialogHeader>

            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
              <div className="space-y-6">
                <div className="grid gap-4 rounded-24 border border-border bg-muted/20 p-5 text-sm md:grid-cols-2">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">수행 유저</p>
                    <p className="text-foreground">{selectedRow.actorDisplay}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">대상 유저</p>
                    <p className="text-foreground">{selectedRow.targetDisplay}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">대상 메뉴</p>
                    <p className="text-foreground">{selectedRow.targetMenuName}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">접속 IP</p>
                    <p className="text-foreground">{selectedRow.ip}</p>
                  </div>
                </div>

                <section className="space-y-3">
                  <div className="flex items-center gap-2">
                    <ChevronRight className="h-4 w-4 text-ruda-primary" />
                    <h2 className="text-sm font-semibold text-foreground">
                      상세 변경 내용
                    </h2>
                  </div>
                  <AuditChangesDetail
                    changes={selectedRow.changes}
                    beforeSnapshot={selectedRow.beforeSnapshot}
                    afterSnapshot={selectedRow.afterSnapshot}
                  />
                </section>
              </div>
            </div>
          </DialogContent>
        ) : null}
      </Dialog>
    </div>
  );
};
